import { QueryStringParams } from '../constants/query-string-params';
import { IFacet } from '../models/IFacet';
import {
  deleteQueryStringParams,
  updateQueryStringValue,
  updateRouteWithQueryString,
} from '../utils/string-helpers/search-query-string-helper';
import { ReadonlyURLSearchParams } from 'next/navigation';

export function useQueryFacets(searchParams: ReadonlyURLSearchParams) {
  const getFacetParamName = (facetId: string): string => QueryStringParams.FacetPrefix + facetId;

  const getFacetTerms = (facetId: string): string => {
    const query = new URLSearchParams(window.location.search);
    return query.get(getFacetParamName(facetId)) ?? ``;
  };

  let searchQueryParams = {};
  for (const [key, value] of searchParams.entries()) {
    searchQueryParams[key] = value;
  }

  const setFacetTerms = (facetId: string, facetTerms: string): void => {
    deleteQueryStringParams(searchQueryParams, [QueryStringParams.Results], true);
    updateQueryStringValue(searchQueryParams, getFacetParamName(facetId), facetTerms);
  };

  const removeFacet = (facetId: string): void =>
    deleteQueryStringParams(searchQueryParams, [getFacetParamName(facetId)]);

  const addFacetTerm = (facetId: string, facetTerm: string) => {
    const facetTerms = getFacetTerms(facetId);
    const facetTermsArray = facetTerms ? facetTerms.split(`,`) : [];
    const updatedFacetTerms = [...facetTermsArray, facetTerm].join(`,`);
    setFacetTerms(facetId, updatedFacetTerms);
  };

  const removeFacetTerm = (facetId: string, facetTerm: string) => {
    const facetTerms = getFacetTerms(facetId);
    const facetTermsArray = facetTerms
      ? facetTerms
          ?.split(`","`)
          .map((a) => a.replaceAll(`"`, ``))
          .map((a) => `"${a}"`)
      : [];
    const updatedFacetTerms = facetTermsArray.filter((ft) => ft !== `"${facetTerm}"`).join(`,`);
    if (updatedFacetTerms) {
      setFacetTerms(facetId, updatedFacetTerms);
    } else {
      removeFacet(facetId);
    }
  };

  const getMappedFacets = (): IFacet[] => {
    const queryString = searchQueryParams;
    const queryKeys = Object.keys(queryString);
    const facets = queryKeys
      .filter((key) => key?.startsWith(QueryStringParams.FacetPrefix))
      .map((key) => {
        const facetTerms = queryString[key].toString();
        const facetTermsArray = facetTerms
          ?.split(`","`)
          .map((a) => a.replaceAll(`"`, ``))
          .map((a) => `"${a}"`);
        const facetId = key.replace(QueryStringParams.FacetPrefix, ``);

        return {
          id: facetId,
          title: facetId,
          values: facetTermsArray,
        };
      });
    return facets;
  };

  const setFacets = (facets: IFacet[]): void => {
    deleteQueryStringParams(
      searchQueryParams,
      [QueryStringParams.Results, QueryStringParams.FacetPrefix],
      true,
    );

    // Convert facet array into parsedUrlQuery object.
    const facetQueryString = facets.reduce(
      // On each reduce() iteration, append a facet as a new property of the object.
      (object, currentFacetVal) => ({
        ...object,
        [getFacetParamName(currentFacetVal.id)]: currentFacetVal.values.join(`,`),
        ...searchQueryParams, // retain the query keyword
      }),
      { ...searchQueryParams }, // retain the query keyword
    );

    updateRouteWithQueryString(facetQueryString);
  };

  return {
    getFacetParamName,
    getFacetTerms,
    setFacetTerms,
    removeFacet,
    addFacetTerm,
    removeFacetTerm,
    getMappedFacets,
    setFacets,
  };
}
